// The bar across the top at a phone's width, and the rail's open state
// (02-DESIGN/03-console/01-the-console.md § the look: at a phone's width; hq
// issue 220): the mark with its live dot, the workspace's name and one
// button, *Menu*. Above 900 px the bar is not drawn and the rail stands
// beside the content column as ever; nothing overlays the page at either
// width. The open state is ./fold.ts's, kept in the history entry.

import { Menu, X } from "lucide-react";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router";

import { useConnection } from "../platform/session.tsx";
import { foldedAt, NARROW, railOpen, withRail, withoutRail } from "./fold.ts";
import { liveOf, Mark } from "./Mark.tsx";

/** RAIL_ID is the rail's element id, which the *Menu* button controls. */
export const RAIL_ID = "rail";

export interface Fold {
  /** Whether the rail is open in place of the page: the entry has it open and the window is under 900 px. */
  open: boolean;
  /** show opens the rail: a new history entry on the same address, so Back closes it. */
  show: () => void;
  /**
   * close is the button's, Escape's and a workspace pick's: the open entry is
   * replaced by the same address with the rail closed, and the page is drawn
   * again from its top.
   */
  close: () => void;
}

function narrowNow(): boolean {
  return typeof window !== "undefined" && window.matchMedia(NARROW).matches;
}

function onWidth(change: () => void): () => void {
  const q = window.matchMedia(NARROW);
  q.addEventListener("change", change);
  return () => {
    q.removeEventListener("change", change);
  };
}

/** useNarrow is whether the window is under 900 px, followed as it changes. */
export function useNarrow(): boolean {
  return useSyncExternalStore(onWidth, narrowNow, () => false);
}

/** useFold is whether the rail is open, kept in the history entry's state as a form is. */
export function useFold(): Fold {
  const location = useLocation();
  const navigate = useNavigate();
  const narrow = useNarrow();
  const here = location.pathname + location.search;
  const state: unknown = location.state;
  // close is stable while the entry is, so what listens for Escape is set once per opening.
  const close = useCallback(() => {
    void navigate(here, { replace: true, state: withoutRail(state) });
  }, [navigate, here, state]);
  return {
    open: foldedAt(railOpen(state), narrow),
    show: () => {
      void navigate(here, { state: withRail(state) });
    },
    close,
  };
}

export function FoldBar({ name, fold }: { name: string; fold: Fold }) {
  const { state } = useConnection();
  const live = liveOf(state);
  const button = useRef<HTMLButtonElement>(null);
  const { open } = fold;
  const { close } = fold;

  // Opened, focus moves into the rail, at the top of the page. Escape closes
  // the rail as it closes the menus and gives focus back to *Menu* — the
  // innermost first: while the workspace menu or the person's menu is open
  // inside the rail, Escape is that menu's. Closed any other way — a pick,
  // Back — the control that held focus is no longer drawn, so focus comes
  // back to *Menu* rather than falling to the page's body. `open` is only
  // ever true under 900 px (foldedAt), so at a wide width nothing listens.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!open) {
      if (wasOpen.current) {
        wasOpen.current = false;
        const at = document.activeElement;
        const rail = document.getElementById(RAIL_ID);
        if (narrowNow() && (at === null || at === document.body || rail?.contains(at) === true)) {
          button.current?.focus();
        }
      }
      return;
    }
    wasOpen.current = true;
    const rail = document.getElementById(RAIL_ID);
    window.scrollTo(0, 0);
    rail?.querySelector<HTMLElement>("a[href], button:not([disabled])")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") {
        return;
      }
      if (rail?.querySelector('[aria-haspopup="menu"][aria-expanded="true"]') != null) {
        return;
      }
      close();
      button.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  return (
    <header className="fold-bar">
      <Mark live={live} />
      <span className="workspace-name">{name}</span>
      {/* The rail's *Reconnecting…* is not drawn while the rail is folded: the bar says it to a screen reader. */}
      {!open && live === "reconnecting" && (
        <span className="visually-hidden" role="status">
          Reconnecting…
        </span>
      )}
      <button
        ref={button}
        type="button"
        className="btn fold-menu"
        aria-expanded={open}
        aria-controls={RAIL_ID}
        onClick={() => {
          if (open) {
            fold.close();
          } else {
            fold.show();
          }
        }}
      >
        {open ? <X strokeWidth={1.6} aria-hidden="true" /> : <Menu strokeWidth={1.6} aria-hidden="true" />}
        Menu
      </button>
    </header>
  );
}
